import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const cardVariants = cva(
  "bg-card text-card-foreground flex flex-col rounded-xl border transition-all duration-200",
  {
    variants: {
      variant: {
        default: "shadow-sm",
        interactive:
          "shadow-sm cursor-pointer hover:shadow-lg hover:border-primary/20 hover:-translate-y-1 active:translate-y-0 active:shadow-md",
        elevated: "shadow-md hover:shadow-xl",
        glass: "bg-card/80 backdrop-blur-sm border-white/10 shadow-sm",
        outline: "shadow-none border-2",
        flat: "shadow-none",
      },
      padding: {
        default: "py-6 gap-6",
        sm: "py-4 gap-4 [&>[data-slot=card-content]]:px-4 [&>[data-slot=card-header]]:px-4 [&>[data-slot=card-footer]]:px-4",
        lg: "py-8 gap-8 [&>[data-slot=card-content]]:px-8 [&>[data-slot=card-header]]:px-8 [&>[data-slot=card-footer]]:px-8",
        none: "py-0 gap-0 [&>[data-slot=card-content]]:px-0 [&>[data-slot=card-header]]:px-0 [&>[data-slot=card-footer]]:px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "default",
    },
  }
)

function Card({
  className,
  variant,
  padding,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant, padding }), className)}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  cardVariants,
}
